import React, { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef } from 'react'
import { Form } from 'react-bootstrap'
import '../styles/quantityPicker.css'

// Main unit label (the case/outer container)
export const getUnitLabel = (uom) => {
    const labels = { film: 'Rolls', pallet: 'Pallets', clear_film: 'Rolls' }
    return labels[uom] || 'Cases'
}

// Sub-unit label (individual pieces)
export const getPieceLabel = (uom) => {
    const labels = { film: 'Impressions', clear_film: 'Grams' }
    return labels[uom] || 'Items'
}

// Singular form of unit label for the info line
export const getUnitLabelSingular = (uom) => {
    const labels = { film: 'roll', pallet: 'pallet', clear_film: 'roll' }
    return labels[uom] || 'case'
}

// Super-unit UOMs: DB stores items (sub-units), not cases.
// QuantityPicker must NOT divide by casePack when calling onChange.
export const isSuperUnit = (uom) => uom === 'pallet' || uom === 'film' || uom === 'clear_film'

// Fractional UOMs: the base unit may be split when casePack <= 1.
//   bulk       -> snapped to FRACTIONAL_PLACES decimals (mirrors backend normalize_quantity)
//   clear_film -> raw decimal grams, no snapping
// Must stay in sync with normalize_quantity() in backend/inventory/views/common.py
export const FRACTIONAL_PLACES = 4
export const isFractionalUom = (uom, casePack = 1) =>
    uom === 'clear_film' || (uom === 'bulk' && (!casePack || casePack <= 1))

// Format max value for display: trim trailing zeros
const formatMax = (maxVal) => {
    const num = parseFloat(maxVal)
    if (isNaN(num)) return ''
    return num.toFixed(4).replace(/\.?0+$/, '')
}

// Build fraction breakdown string: "11 600/1100" or "300/1100" or "" (exact)
const formatMaxBreakdown = (displayMax, casePack) => {
    const totalPieces = Math.round(parseFloat(displayMax) * casePack)
    const wholePart = Math.floor(totalPieces / casePack)
    const remainder = totalPieces % casePack
    if (remainder === 0) return ''
    if (wholePart === 0) return `${remainder}/${casePack}`
    return `${wholePart} ${remainder}/${casePack}`
}

const QuantityPicker = forwardRef(({ casePack, uom, value, onChange, required, max }, ref) => {
    const useSplit = casePack > 1
    const superUnit = isSuperUnit(uom)
    const simpleInputRef = useRef(null)
    const splitFirstInputRef = useRef(null)

    useImperativeHandle(ref, () => ({
        focus: () => {
            if (useSplit) {
                splitFirstInputRef.current?.focus()
            } else {
                simpleInputRef.current?.focus()
            }
        }
    }))

    // For split mode: units and pieces as separate integer fields
    const [units, setUnits] = useState('')
    const [pieces, setPieces] = useState('')
    const [total, setTotal] = useState('')
    // Track which field was last edited to avoid circular updates
    const [lastEdited, setLastEdited] = useState(null)

    const unitLabel = getUnitLabel(uom)
    const pieceLabel = getPieceLabel(uom)
    const unitLabelSingular = getUnitLabelSingular(uom)

    const hasMax = max !== undefined && max !== null && max !== ''
    const maxNum = hasMax ? parseFloat(max) : null
    const displayMax = hasMax ? formatMax(superUnit ? maxNum / casePack : maxNum) : ''

    // Convert decimal case quantity to units/pieces when value changes externally
    useEffect(() => {
        if (!useSplit || lastEdited) return
        if (value === '' || value === null || value === undefined) {
            setUnits('')
            setPieces('')
            setTotal('')
            return
        }
        const totalPieces = superUnit ? Math.round(parseFloat(value)) : Math.round(parseFloat(value) * casePack)
        const u = Math.floor(totalPieces / casePack)
        const p = totalPieces % casePack
        setUnits(u > 0 ? String(u) : '')
        setPieces(p > 0 ? String(p) : '')
        const decimal = parseFloat((totalPieces / casePack).toFixed(4))
        setTotal(decimal > 0 ? String(decimal) : '')
    }, [value, casePack, useSplit, lastEdited, superUnit])

    // Clamp totalPieces to max (in pieces) if max is set
    const clampToPieces = useCallback((totalPieces) => {
        if (hasMax && maxNum !== null) {
            const maxPieces = superUnit ? Math.floor(maxNum) : Math.round(maxNum * casePack)
            if (totalPieces > maxPieces) return maxPieces
        }
        return totalPieces
    }, [hasMax, maxNum, casePack, superUnit])

    // Compute total and call onChange from units + pieces
    const updateFromUnitsPieces = useCallback((rawUnits, rawPieces) => {
        const u = parseInt(rawUnits) || 0
        const p = parseInt(rawPieces) || 0
        let totalPieces = u * casePack + p

        if (totalPieces < 0) totalPieces = 0
        totalPieces = clampToPieces(totalPieces)

        // Auto-carry: normalize pieces >= casePack
        const normalizedUnits = Math.floor(totalPieces / casePack)
        const normalizedPieces = totalPieces % casePack

        setUnits(normalizedUnits > 0 ? String(normalizedUnits) : (rawUnits === '' ? '' : '0'))
        setPieces(normalizedPieces > 0 ? String(normalizedPieces) : (rawPieces === '' ? '' : '0'))
        const decimal = parseFloat((totalPieces / casePack).toFixed(4))
        setTotal(totalPieces > 0 ? String(decimal) : '')

        if (totalPieces === 0 && rawUnits === '' && rawPieces === '') {
            onChange('')
        } else {
            onChange(superUnit ? String(totalPieces) : (totalPieces / casePack).toFixed(6))
        }
    }, [casePack, onChange, clampToPieces, superUnit])

    // Compute units + pieces from decimal total
    const updateFromTotal = useCallback((rawTotal) => {
        const parsed = parseFloat(rawTotal)
        if (isNaN(parsed) || parsed < 0) {
            if (rawTotal === '') {
                setUnits('')
                setPieces('')
                onChange('')
            }
            return
        }

        let totalPieces = Math.round(parsed * casePack)
        totalPieces = clampToPieces(totalPieces)
        const u = Math.floor(totalPieces / casePack)
        const p = totalPieces % casePack

        setUnits(u > 0 ? String(u) : '')
        setPieces(p > 0 ? String(p) : '')

        if (totalPieces === 0 && rawTotal === '') {
            onChange('')
        } else {
            onChange(superUnit ? String(totalPieces) : (totalPieces / casePack).toFixed(6))
        }
    }, [casePack, onChange, clampToPieces, superUnit])

    const handleUnitsChange = (e) => {
        const val = e.target.value
        setUnits(val)
        setLastEdited('units')
        // Defer normalization to blur
    }

    const handlePiecesChange = (e) => {
        const val = e.target.value
        setPieces(val)
        setLastEdited('pieces')
        // Defer normalization to blur
    }

    const handleTotalChange = (e) => {
        const val = e.target.value
        setTotal(val)
        setLastEdited('total')
        updateFromTotal(val)
    }

    const handleUnitsBlur = () => {
        updateFromUnitsPieces(units, pieces)
        setLastEdited(null)
    }

    const handlePiecesBlur = () => {
        updateFromUnitsPieces(units, pieces)
        setLastEdited(null)
    }

    const handleTotalBlur = () => {
        // In split mode, clamp on blur when max is set
        if (hasMax) {
            updateFromTotal(total)
        }
        setLastEdited(null)
    }

    // Live update total as user types in units/pieces (without normalizing yet)
    useEffect(() => {
        if (lastEdited === 'units' || lastEdited === 'pieces') {
            const u = parseInt(units) || 0
            const p = parseInt(pieces) || 0
            const totalPieces = u * casePack + p
            const decimal = parseFloat((totalPieces / casePack).toFixed(4))
            setTotal(totalPieces > 0 ? String(decimal) : '')

            // Also call onChange so the parent form has the current value
            if (totalPieces === 0 && units === '' && pieces === '') {
                onChange('')
            } else {
                onChange(superUnit ? String(totalPieces) : (totalPieces / casePack).toFixed(6))
            }
        }
    }, [units, pieces, lastEdited, casePack, onChange, superUnit])

    const breakdown = hasMax && useSplit ? formatMaxBreakdown(displayMax, casePack) : ''

    const handleFillMax = useCallback(() => {
        if (!hasMax) return
        const totalPieces = superUnit ? Math.floor(maxNum) : Math.round(maxNum * casePack)
        const u = Math.floor(totalPieces / casePack)
        const p = totalPieces % casePack
        setUnits(u > 0 ? String(u) : '')
        setPieces(p > 0 ? String(p) : '')
        const decimal = parseFloat((totalPieces / casePack).toFixed(4))
        setTotal(totalPieces > 0 ? String(decimal) : '')
        setLastEdited(null)
        onChange(superUnit ? String(totalPieces) : (totalPieces / casePack).toFixed(6))
    }, [hasMax, maxNum, casePack, superUnit, onChange])

    const handleFillSimpleMax = useCallback(() => {
        if (!hasMax) return
        onChange(String(maxNum))
    }, [hasMax, maxNum, onChange])

    // Simple mode: single quantity input
    // bulk (casePack <= 1) and clear_film allow fractions; all other UOMs require whole numbers.
    const allowFractions = isFractionalUom(uom, casePack)
    // bulk snaps to the 4-decimal grid on blur (clear_film stays free-form, matching the backend passthrough)
    const snapOnBlur = allowFractions && uom === 'bulk'
    const handleSimpleBlur = (e) => {
        if (!snapOnBlur) return
        const raw = e.target.value
        if (raw === '' || raw === null || raw === undefined) return
        let num = parseFloat(raw)
        if (isNaN(num)) return
        num = parseFloat(num.toFixed(FRACTIONAL_PLACES))
        if (hasMax && num > maxNum) num = maxNum
        if (String(num) !== String(raw)) onChange(String(num))
    }
    if (!useSplit) {
        return (
            <Form.Group className="mb-4" controlId="quantity">
                <Form.Control
                    ref={simpleInputRef}
                    type="number"
                    min={allowFractions ? '0.0001' : '1'}
                    step={allowFractions ? 'any' : '1'}
                    value={value !== '' && value !== null && value !== undefined ? parseFloat(value) : ''}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={handleSimpleBlur}
                    required={required}
                    placeholder="Enter quantity"
                    autoComplete="off"
                    spellCheck="false"
                    {...(hasMax ? { max: maxNum } : {})}
                />
                {hasMax && (
                    <div className="qp-info-line">
                        <span>max: {formatMax(maxNum)}</span>
                        <button className="qp-all-pill" onClick={handleFillSimpleMax} type="button">all</button>
                    </div>
                )}
            </Form.Group>
        )
    }

    // Split mode: Units / Pieces / Total
    return (
        <Form.Group className="mb-4">
            <div className="qp-split-wrapper">
                <div className="qp-split-row">
                    <label className="qp-split-cell">
                        <span className="qp-label">{unitLabel}</span>
                        <input
                            ref={splitFirstInputRef}
                            type="number"
                            min="0"
                            step="1"
                            value={units}
                            onChange={handleUnitsChange}
                            onBlur={handleUnitsBlur}
                            placeholder="0"
                            autoComplete="off"
                        />
                    </label>
                    <label className="qp-split-cell">
                        <span className="qp-label">{pieceLabel}</span>
                        <input
                            type="number"
                            min="0"
                            step="1"
                            value={pieces}
                            onChange={handlePiecesChange}
                            onBlur={handlePiecesBlur}
                            placeholder="0"
                            autoComplete="off"
                        />
                    </label>
                    <label className="qp-split-cell qp-total-cell">
                        <span className="qp-label">Total ({unitLabel})</span>
                        <input
                            type="number"
                            min="0"
                            step="any"
                            value={total}
                            onChange={handleTotalChange}
                            onBlur={handleTotalBlur}
                            placeholder="0"
                            autoComplete="off"
                        />
                    </label>
                </div>
                {hasMax ? (
                    <div className="qp-info-line">
                        <span>max: {displayMax}{breakdown && ` (${breakdown})`}</span>
                        <button className="qp-all-pill" onClick={handleFillMax} type="button">all</button>
                    </div>
                ) : null}
            </div>
            {/* Hidden input for form validation */}
            {required && (
                <input
                    type="hidden"
                    value={value || ''}
                    required
                />
            )}
        </Form.Group>
    )
})

QuantityPicker.displayName = 'QuantityPicker'

export default QuantityPicker
